import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError, authApi } from '../lib/api';
import { installGlobalKeyListener } from '../lib/keys';
import { useSessionStore } from '../stores/session';
import { useCompanyStore } from '../stores/company';
import { ToastStack } from '../components/Toast';
import { LoginPage } from '../features/auth/LoginPage';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { AppShell } from './AppShell';
import { ChartOfAccountsPage } from '../features/coa/ChartOfAccountsPage';
import { CompanyFeaturesPage } from '../features/company/CompanyFeaturesPage';
import { CompanyCreateWizard } from '../features/company/CompanyCreateWizard';
import { BranchesPage } from '../features/company/BranchesPage';
import { FinancialYearsPage } from '../features/company/FinancialYearsPage';
import { PeriodLocksPage } from '../features/company/PeriodLocksPage';
import { UsersPage } from '../features/users/UsersPage';
import { RolesPage } from '../features/users/RolesPage';
import { ActivityPage } from '../features/audit/ActivityPage';
import { SystemHealthPage } from '../features/system/SystemHealthPage';
import { VoucherWorkspacePage } from '../features/vouchers/VoucherWorkspacePage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { OnboardingPage } from '../features/onboarding/OnboardingPage';
import {
  CatalogSetupPage,
  InvoiceSeriesPage,
  ItemsPage,
  PartyAddressesPage,
  PostingAccountsPage,
  PrintTemplatesPage,
  VoucherClassesPage,
} from '../features/masters/MasterDataPages';
import { GoodsVoucherWorkspacePage } from '../features/goods/GoodsVoucherWorkspacePage';
import { GoodsVoucherRegisterPage } from '../features/goods/GoodsVoucherRegisterPage';
import { GoodsReturnWorkspacePage } from '../features/goods/GoodsReturnWorkspacePage';
import { MoneyDeskPage } from '../features/money/MoneyDeskPage';

installGlobalKeyListener();

function RequireAuth({ children }: { children: React.ReactElement }) {
  const status = useSessionStore((s) => s.status);
  const mustChangePassword = useSessionStore((s) => s.user?.mustChangePassword);
  const location = useLocation();

  if (status === 'checking') {
    return (
      <div className="flex h-screen items-center justify-center text-[12.5px] text-[var(--text-dim)]">Loading…</div>
    );
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function RequireCompany({ children }: { children: React.ReactElement }) {
  const companies = useCompanyStore((s) => s.companies);
  const status = useSessionStore((s) => s.status);
  const location = useLocation();
  if (status === 'authenticated' && companies.length === 0 && location.pathname !== '/companies/new') {
    return <Navigate to="/companies/new" replace state={{ from: location }} />;
  }
  return children;
}

export function App() {
  const setSession = useSessionStore((s) => s.setSession);
  const setCompanies = useCompanyStore((s) => s.setCompanies);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);

  useEffect(() => {
    let cancelled = false;
    async function restoreSession() {
      try {
        return await authApi.session();
      } catch (error) {
        // A company can be deactivated or removed from a user's access while its last
        // selection remains in local storage. Drop only that preference and retry the
        // session request without a company header; do not mistake a valid login for an
        // expired one.
        if (error instanceof ApiError && error.code === 'COMPANY_NOT_ACCESSIBLE') {
          setActiveCompanyId(null);
          return authApi.session();
        }
        throw error;
      }
    }

    restoreSession()
      .then((payload) => {
        if (cancelled) return;
        setSession(payload);
        setCompanies(payload.companies);
        setActiveCompanyId(payload.activeCompanyId);
      })
      .catch(() => {
        if (!cancelled) useSessionStore.setState({ status: 'anonymous' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/change-password"
          element={
            <RequireAuth>
              <ChangePasswordPage />
            </RequireAuth>
          }
        />
        <Route
          path="/companies/new"
          element={
            <RequireAuth>
              <CompanyCreateWizard />
            </RequireAuth>
          }
        />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <RequireCompany>
                <AppShell />
              </RequireCompany>
            </RequireAuth>
          }
        >
          <Route index element={<ChartOfAccountsPage />} />
          <Route path="vouchers" element={<VoucherWorkspacePage />} />
          <Route path="vouchers/new" element={<VoucherWorkspacePage />} />
          <Route path="vouchers/:id" element={<VoucherWorkspacePage />} />
          <Route path="money" element={<MoneyDeskPage />} />
          <Route path="money/receipt/new" element={<MoneyDeskPage view="receipt" />} />
          <Route path="money/payment/new" element={<MoneyDeskPage view="payment" />} />
          <Route path="money/debit-note/new" element={<MoneyDeskPage view="debit_note" />} />
          <Route path="money/credit-note/new" element={<MoneyDeskPage view="credit_note" />} />
          <Route path="goods/debit-note/new" element={<GoodsReturnWorkspacePage kind="debit_note" />} />
          <Route path="goods/debit-note/:id" element={<GoodsVoucherWorkspacePage direction="purchase" />} />
          <Route path="goods/credit-note/new" element={<GoodsReturnWorkspacePage kind="credit_note" />} />
          <Route path="goods/credit-note/:id" element={<GoodsVoucherWorkspacePage direction="sales" />} />
          <Route path="reports/outstandings" element={<MoneyDeskPage view="outstandings" />} />
          <Route path="reports/cash-book" element={<MoneyDeskPage view="cash-book" />} />
          <Route path="reports/bank-book" element={<MoneyDeskPage view="bank-book" />} />
          <Route path="reports/pdc" element={<MoneyDeskPage view="pdc" />} />
          <Route path="reports/bank-reconciliation" element={<MoneyDeskPage view="reconciliation" />} />
          <Route path="goods/purchase/new" element={<GoodsVoucherWorkspacePage direction="purchase" />} />
          <Route path="goods/purchase/:id" element={<GoodsVoucherWorkspacePage direction="purchase" />} />
          <Route path="goods/purchase/register" element={<GoodsVoucherRegisterPage direction="purchase" />} />
          <Route path="goods/sales/new" element={<GoodsVoucherWorkspacePage direction="sales" />} />
          <Route path="goods/sales/:id" element={<GoodsVoucherWorkspacePage direction="sales" />} />
          <Route path="goods/sales/register" element={<GoodsVoucherRegisterPage direction="sales" />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/balance-sheet" element={<ReportsPage view="balance_sheet" />} />
          <Route path="reports/trial-balance" element={<ReportsPage view="trial_balance" />} />
          <Route path="reports/day-book" element={<ReportsPage view="day_book" />} />
          <Route path="reports/ledger" element={<ReportsPage view="ledger" />} />
          <Route path="reports/group-summary" element={<ReportsPage view="group_summary" />} />
          <Route path="coa" element={<ChartOfAccountsPage />} />
          <Route path="masters/items" element={<ItemsPage />} />
          <Route path="masters/catalog" element={<CatalogSetupPage />} />
          <Route path="masters/party-addresses" element={<PartyAddressesPage />} />
          <Route path="settings/voucher-classes" element={<VoucherClassesPage />} />
          <Route path="settings/invoice-series" element={<InvoiceSeriesPage />} />
          <Route path="settings/posting-accounts" element={<PostingAccountsPage />} />
          <Route path="settings/print-templates" element={<PrintTemplatesPage />} />
          <Route path="company/features" element={<CompanyFeaturesPage />} />
          <Route path="company/branches" element={<BranchesPage />} />
          <Route path="company/financial-years" element={<FinancialYearsPage />} />
          <Route path="company/period-locks" element={<PeriodLocksPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="audit" element={<ActivityPage />} />
          <Route path="system/health" element={<SystemHealthPage />} />
          <Route path="*" element={<div className="p-4 text-[12.5px] text-[var(--text-dim)]">Not found.</div>} />
        </Route>
      </Routes>
      <ToastStack />
    </>
  );
}
